import { c as _c } from "react/compiler-runtime";
import { add, setMilliseconds, startOfHour, sub } from 'date-fns';
import { useContext, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import { extractFromArray } from '../../../../utils';
import { relative_dates } from '../../../../utils/date';
import Divider from '../../../common/base/Divider';
import Icon from '../../../common/base/Icon';
import Text from '../../../common/base/Text';
import ErrorBoundary from '../../../common/helpers/ErrorBoundary';
import { StyledCentered } from '../../../common/helpers/StyledCentered';
import { Div } from '../../../common/helpers/StyledUtils';
import type { FilterComponent, FilterFormValues } from '../../../common/Filters/FilterComponents';
import * as FilterComponents from '../../../common/Filters/FilterComponents';
import Filters from '../../../common/Filters/Filters';
import { TableProvider } from '../../../contexts/TableContext';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { useIsScrolled } from '../../../hooks/useIsScrolled';
import useSearchQuery from '../../../hooks/useSearchQuery';
import { StyledViewContent, StyledViewLayout, StyledViewNav, StyledViewNavSecondary } from '../../../layouts/ViewLayout';
import { DateFilter } from '../../DashboardScene/Events/Histogram';
import SuggestedColumns from '../../DashboardScene/Requests/SuggestedColumns';
import OperatorEventAttemptSidebar from './OperatorEventAttemptSidebar';
import AttemptsList from '../Attempts/AttemptsList';
import useOperatorEventRetryAttempt from './useOperatorEventRetryAttempt';
import { DEFAULT_DATE, OutpostAttemptQueryParams, OUTPOST_ATTEMPT_INCLUDES, buildOutpostAttemptListParams, extractAttemptFiltersFromQuery } from '../Attempts/utils';
const POLL_NEW_ATTEMPTS_INTERVAL = 5000;
const filter_components = (FilterComponents as Record<string, unknown>)['outpost_attempt_list_filters'] as FilterComponent[];
const OperatorEventDeliveriesView: React.FC<{
  destination_id: string;
}> = t0 => {
  const $ = _c(151);
  const {
    destination_id
  } = t0;
  const {
    query,
    updateSearchQuery
  } = useSearchQuery();
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    has_scrolled,
    scrolled_element_ref
  } = useIsScrolled();
  const [poll_new_attempts_ready, setPollNewAttemptsReady] = useState(false);
  const current_page = query.current_page ? parseInt(query.current_page, 10) : 1;
  let t1;
  if ($[0] !== query.selected_attempt_id) {
    t1 = extractFromArray(query.selected_attempt_id);
    $[0] = query.selected_attempt_id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selected_attempt_id = t1 as string;
  let t2;
  if ($[2] !== query.selected_history_attempt_id) {
    t2 = extractFromArray(query.selected_history_attempt_id);
    $[2] = query.selected_history_attempt_id;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const selected_history_attempt_id = t2 as string;
  let new_filters;
  if ($[4] !== query) {
    new_filters = extractAttemptFiltersFromQuery(query);
    if (!query.date) {
      let t3;
      let t4;
      let t5;
      if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
        const {
          min,
          max
        } = relative_dates[DEFAULT_DATE].convert(new Date());
        t3 = DEFAULT_DATE;
        t4 = min?.toISOString();
        t5 = max?.toISOString();
        $[6] = t3;
        $[7] = t4;
        $[8] = t5;
      } else {
        t3 = $[6];
        t4 = $[7];
        t5 = $[8];
      }
      let t6;
      if ($[9] !== t4 || $[10] !== t5) {
        t6 = {
          relative: t3,
          min: t4,
          max: t5
        };
        $[9] = t4;
        $[10] = t5;
        $[11] = t6;
      } else {
        t6 = $[11];
      }
      new_filters.date = t6;
    }
    $[4] = query;
    $[5] = new_filters;
  } else {
    new_filters = $[5];
  }
  const filters = new_filters;
  let t3;
  bb0: {
    if ("dynamic_columns" in query) {
      t3 = query.dynamic_columns === null ? undefined : query.dynamic_columns;
      break bb0;
    }
    t3 = undefined;
  }
  const dynamic_columns = t3;
  let t4;
  if ($[12] !== destination_id || $[13] !== filters) {
    t4 = buildOutpostAttemptListParams({
      ...filters,
      destination_id
    });
    $[12] = destination_id;
    $[13] = filters;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const api_filters = t4;
  let t5;
  if ($[15] !== api_filters) {
    t5 = APIMethodKeys.outpost.operatorEvents.attempts.list(api_filters);
    $[15] = api_filters;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== HookdeckAPI.outpost.operatorEvents.attempts || $[18] !== api_filters) {
    t6 = () => HookdeckAPI.outpost.operatorEvents.attempts.list(api_filters);
    $[17] = HookdeckAPI.outpost.operatorEvents.attempts;
    $[18] = api_filters;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      revalidateOnFocus: false
    };
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const {
    data: attempts_response,
    mutate: mutateAttempts,
    isLoading: is_loading
  } = useSWR(t5, t6, t7);
  let attempts;
  let selected_attempt;
  let t8;
  if ($[21] !== attempts_response?.models || $[22] !== selected_attempt_id) {
    attempts = attempts_response?.models || [];
    let t9;
    if ($[26] !== selected_attempt_id) {
      t9 = attempt => attempt.id === selected_attempt_id;
      $[26] = selected_attempt_id;
      $[27] = t9;
    } else {
      t9 = $[27];
    }
    selected_attempt = attempts.find(t9);
    t8 = selected_attempt?.event_id ? APIMethodKeys.outpost.operatorEvents.attempts.list({
      event_id: selected_attempt.event_id,
      include: OUTPOST_ATTEMPT_INCLUDES
    }) : null;
    $[21] = attempts_response?.models;
    $[22] = selected_attempt_id;
    $[23] = attempts;
    $[24] = selected_attempt;
    $[25] = t8;
  } else {
    attempts = $[23];
    selected_attempt = $[24];
    t8 = $[25];
  }
  const selected_attempt_history_key = t8;
  let t9;
  if ($[28] !== HookdeckAPI.outpost.operatorEvents.attempts || $[29] !== destination_id || $[30] !== selected_attempt?.event_id) {
    t9 = () => HookdeckAPI.outpost.operatorEvents.attempts.list({
      event_id: selected_attempt?.event_id,
      destination_id,
      include: OUTPOST_ATTEMPT_INCLUDES
    });
    $[28] = HookdeckAPI.outpost.operatorEvents.attempts;
    $[29] = destination_id;
    $[30] = selected_attempt?.event_id;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      refreshInterval: 5000
    };
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  const {
    data: selected_attempt_history_response,
    mutate: mutateSelectedAttemptHistory
  } = useSWR(selected_attempt_history_key, t9, t10);
  let t11;
  if ($[33] !== attempts || $[34] !== selected_attempt_history_response?.models || $[35] !== selected_history_attempt_id) {
    t11 = selected_attempt_history_response?.models.find(attempt_1 => attempt_1.id === selected_history_attempt_id) || attempts.find(attempt_0 => attempt_0.id === selected_history_attempt_id);
    $[33] = attempts;
    $[34] = selected_attempt_history_response?.models;
    $[35] = selected_history_attempt_id;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  const selected_history_attempt = t11;
  const sidebar_attempt_id = selected_history_attempt_id || selected_attempt_id;
  const sidebar_attempt = selected_history_attempt || selected_attempt;
  let t12;
  if ($[37] !== mutateAttempts || $[38] !== mutateSelectedAttemptHistory) {
    t12 = async () => {
      await Promise.all([mutateAttempts(), mutateSelectedAttemptHistory?.()]);
    };
    $[37] = mutateAttempts;
    $[38] = mutateSelectedAttemptHistory;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  const {
    retryAttempt,
    retrying_attempt_id
  } = useOperatorEventRetryAttempt(t12);
  filters.date?.relative;
  let t13;
  bb1: {
    const poll_after_timestamp = filters.date?.relative && !attempts_response?.pagination.prev ? attempts_response?.models[0]?.time : null;
    if (!poll_after_timestamp) {
      t13 = null;
      break bb1;
    }
    let t14;
    if ($[40] !== poll_after_timestamp) {
      t14 = new Date(new Date(poll_after_timestamp).getTime() + 1000).toISOString();
      $[40] = poll_after_timestamp;
      $[41] = t14;
    } else {
      t14 = $[41];
    }
    let t15;
    if ($[42] !== t14) {
      t15 = {
        gt: t14
      };
      $[42] = t14;
      $[43] = t15;
    } else {
      t15 = $[43];
    }
    let t16;
    if ($[44] !== api_filters || $[45] !== t15) {
      t16 = {
        ...api_filters,
        time: t15,
        limit: 10,
        next: undefined,
        prev: undefined
      };
      $[44] = api_filters;
      $[45] = t15;
      $[46] = t16;
    } else {
      t16 = $[46];
    }
    t13 = t16;
  }
  const poll_new_attempts_filters = t13;
  let t14;
  let t15;
  if ($[47] !== poll_new_attempts_filters) {
    t14 = () => {
      if (!poll_new_attempts_filters) {
        setPollNewAttemptsReady(false);
        return;
      }
      const timeout_id = window.setTimeout(() => {
        setPollNewAttemptsReady(true);
      }, POLL_NEW_ATTEMPTS_INTERVAL);
      return () => window.clearTimeout(timeout_id);
    };
    t15 = [poll_new_attempts_filters];
    $[47] = poll_new_attempts_filters;
    $[48] = t14;
    $[49] = t15;
  } else {
    t14 = $[48];
    t15 = $[49];
  }
  useEffect(t14, t15);
  const should_poll_new_attempts = poll_new_attempts_filters && poll_new_attempts_ready;
  let t16;
  if ($[50] !== poll_new_attempts_filters || $[51] !== should_poll_new_attempts) {
    t16 = should_poll_new_attempts && APIMethodKeys.outpost.operatorEvents.attempts.list(poll_new_attempts_filters);
    $[50] = poll_new_attempts_filters;
    $[51] = should_poll_new_attempts;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  let t17;
  if ($[53] !== HookdeckAPI.outpost.operatorEvents.attempts || $[54] !== poll_new_attempts_filters) {
    t17 = () => {
      if (!poll_new_attempts_filters) {
        throw new Error("Polling filters are required to load new attempts.");
      }
      return HookdeckAPI.outpost.operatorEvents.attempts.list(poll_new_attempts_filters);
    };
    $[53] = HookdeckAPI.outpost.operatorEvents.attempts;
    $[54] = poll_new_attempts_filters;
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  let t18;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      refreshInterval: POLL_NEW_ATTEMPTS_INTERVAL,
      revalidateOnFocus: true
    };
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  useSWR(t16, t17, t18);
  let end_date;
  let t19;
  if ($[57] !== filters.date) {
    const now = new Date();
    end_date = setMilliseconds(filters.date?.max ? new Date(filters.date.max) : add(startOfHour(now), {
      hours: 1
    }), 0);
    t19 = setMilliseconds(filters.date?.min ? new Date(filters.date.min) : sub(add(startOfHour(now), {
      hours: 1
    }), {
      days: 1
    }), 0);
    $[57] = filters.date;
    $[58] = end_date;
    $[59] = t19;
  } else {
    end_date = $[58];
    t19 = $[59];
  }
  const start_date = t19;
  let t20;
  if ($[60] !== end_date || $[61] !== start_date) {
    t20 = [start_date, end_date];
    $[60] = end_date;
    $[61] = start_date;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  const [stats_start_date, stats_end_date] = t20;
  let t21;
  if ($[63] !== updateSearchQuery) {
    t21 = id => updateSearchQuery({
      selected_attempt_id: id || undefined
    }, {
      remove_keys: id ? ["selected_history_attempt_id"] : ["selected_attempt_id", "selected_history_attempt_id"]
    });
    $[63] = updateSearchQuery;
    $[64] = t21;
  } else {
    t21 = $[64];
  }
  const onAttemptSelected = t21;
  let t22;
  if ($[65] !== updateSearchQuery) {
    t22 = id_0 => updateSearchQuery({
      selected_history_attempt_id: id_0
    });
    $[65] = updateSearchQuery;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  const onHistoryAttemptSelected = t22;
  let t23;
  if ($[67] !== attempts || $[68] !== onAttemptSelected || $[69] !== selected_attempt_id) {
    t23 = () => {
      if (!selected_attempt_id || !attempts.length) {
        return;
      }
      const current_index = attempts.findIndex(attempt_2 => attempt_2.id === selected_attempt_id);
      if (current_index > 0) {
        onAttemptSelected(attempts[current_index - 1].id);
      }
    };
    $[67] = attempts;
    $[68] = onAttemptSelected;
    $[69] = selected_attempt_id;
    $[70] = t23;
  } else {
    t23 = $[70];
  }
  const onPrevAttempt = t23;
  let t24;
  if ($[71] !== attempts || $[72] !== onAttemptSelected || $[73] !== selected_attempt_id) {
    t24 = () => {
      if (!selected_attempt_id || !attempts.length) {
        return;
      }
      const current_index_0 = attempts.findIndex(attempt_3 => attempt_3.id === selected_attempt_id);
      if (current_index_0 < attempts.length - 1) {
        onAttemptSelected(attempts[current_index_0 + 1].id);
      }
    };
    $[71] = attempts;
    $[72] = onAttemptSelected;
    $[73] = selected_attempt_id;
    $[74] = t24;
  } else {
    t24 = $[74];
  }
  const onNextAttempt = t24;
  let t25;
  if ($[75] !== dynamic_columns || $[76] !== selected_attempt_id || $[77] !== selected_history_attempt_id || $[78] !== updateSearchQuery) {
    t25 = new_filters_0 => {
      updateSearchQuery({
        ...new_filters_0,
        date: new_filters_0.date?.relative ? new_filters_0.date.relative : new_filters_0.date,
        selected_attempt_id,
        selected_history_attempt_id,
        dynamic_columns,
        current_page: undefined
      }, {
        remove_keys: ["next", "prev", "current_page"],
        replace: true
      });
    };
    $[75] = dynamic_columns;
    $[76] = selected_attempt_id;
    $[77] = selected_history_attempt_id;
    $[78] = updateSearchQuery;
    $[79] = t25;
  } else {
    t25 = $[79];
  }
  const onFilterChanged = t25;
  let t26;
  if ($[80] !== filters || $[81] !== selected_attempt_id || $[82] !== selected_history_attempt_id || $[83] !== updateSearchQuery) {
    t26 = date => {
      if (!date.relative) {
        delete date.relative;
      }
      updateSearchQuery({
        ...filters,
        date: date.relative ? date.relative : date,
        selected_attempt_id,
        selected_history_attempt_id
      }, {
        remove_keys: ["next", "prev", "current_page"]
      });
    };
    $[80] = filters;
    $[81] = selected_attempt_id;
    $[82] = selected_history_attempt_id;
    $[83] = updateSearchQuery;
    $[84] = t26;
  } else {
    t26 = $[84];
  }
  const setDate = t26;
  let t27;
  if ($[85] !== dynamic_columns) {
    t27 = {
      dynamic_columns
    };
    $[85] = dynamic_columns;
    $[86] = t27;
  } else {
    t27 = $[86];
  }
  let t28;
  let t29;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = {
      align: "flex-start",
      gap: 3
    };
    t29 = {
      x: 6,
      y: 3
    };
    $[87] = t28;
    $[88] = t29;
  } else {
    t28 = $[87];
    t29 = $[88];
  }
  let t30;
  if ($[89] !== filters || $[90] !== setDate || $[91] !== stats_end_date || $[92] !== stats_start_date) {
    t30 = <DateFilter filters={filters} setDate={setDate} start_date={stats_start_date} end_date={stats_end_date} />;
    $[89] = filters;
    $[90] = setDate;
    $[91] = stats_end_date;
    $[92] = stats_start_date;
    $[93] = t30;
  } else {
    t30 = $[93];
  }
  let t31;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <Divider vertical={true} h={{
      px: 28
    }} />;
    $[94] = t31;
  } else {
    t31 = $[94];
  }
  let t32;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <Div w={100}><Filters.Form /></Div>;
    $[95] = t32;
  } else {
    t32 = $[95];
  }
  let t33;
  if ($[96] !== t30) {
    t33 = <StyledViewNav><StyledViewNavSecondary p={0}><Div flex={t28} p={t29}>{t30}{t31}{t32}</Div></StyledViewNavSecondary></StyledViewNav>;
    $[96] = t30;
    $[97] = t33;
  } else {
    t33 = $[97];
  }
  let t34;
  let t35;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = {
      overflow: "auto",
      height: "calc(100vh - 106px)",
      position: "relative"
    };
    t35 = {
      direction: "column"
    };
    $[98] = t34;
    $[99] = t35;
  } else {
    t34 = $[98];
    t35 = $[99];
  }
  let t36;
  if ($[100] !== attempts) {
    t36 = attempts.length > 0 && <><Div flex={{
        align: "flex-start",
        gap: 3
      }} p={{
        x: 6,
        y: 3
      }}><Div flex={{
          align: "center",
          gap: 2
        }} h={{
          px: 28
        }} m={{
          r: 4.5
        }}><Icon icon="add_column_right" muted={true} /><Text semi={true} muted={true} text_wrap={false}>Suggested columns</Text></Div><Divider vertical={true} /><Div w={100}><SuggestedColumns entity="outpost_attempts" models={attempts} /></Div></Div><Divider /></>;
    $[100] = attempts;
    $[101] = t36;
  } else {
    t36 = $[101];
  }
  let t37;
  if ($[102] !== t36) {
    t37 = <StyledCentered>{t36}</StyledCentered>;
    $[102] = t36;
    $[103] = t37;
  } else {
    t37 = $[103];
  }
  let t38;
  if ($[104] !== attempts_response || $[105] !== current_page || $[106] !== updateSearchQuery) {
    t38 = attempts_response?.pagination?.next ? () => {
      updateSearchQuery({
        next: attempts_response.pagination.next || "",
        prev: undefined,
        current_page: String(current_page + 1)
      });
    } : undefined;
    $[104] = attempts_response;
    $[105] = current_page;
    $[106] = updateSearchQuery;
    $[107] = t38;
  } else {
    t38 = $[107];
  }
  let t39;
  if ($[108] !== attempts_response?.pagination?.prev || $[109] !== current_page || $[110] !== updateSearchQuery) {
    t39 = current_page > 1 ? () => {
      updateSearchQuery({
        prev: attempts_response?.pagination?.prev || undefined,
        next: undefined,
        current_page: String(current_page - 1)
      });
    } : undefined;
    $[108] = attempts_response?.pagination?.prev;
    $[109] = current_page;
    $[110] = updateSearchQuery;
    $[111] = t39;
  } else {
    t39 = $[111];
  }
  let t40;
  if ($[112] !== attempts_response || $[113] !== current_page || $[114] !== is_loading || $[115] !== onAttemptSelected || $[116] !== onHistoryAttemptSelected || $[117] !== retryAttempt || $[118] !== retrying_attempt_id || $[119] !== selected_attempt_history_response || $[120] !== selected_attempt_id || $[121] !== selected_history_attempt_id || $[122] !== t38 || $[123] !== t39) {
    t40 = <ErrorBoundary><AttemptsList context="operator-events" attempts_response={attempts_response} history_attempts_response={selected_attempt_history_response} loading={is_loading} selected_attempt_id={selected_attempt_id} selected_history_attempt_id={selected_history_attempt_id} onAttemptSelected={onAttemptSelected} onHistoryAttemptSelected={onHistoryAttemptSelected} onRetryAttempt={retryAttempt} retrying_attempt_id={retrying_attempt_id} current_page={current_page} onNextPage={t38} onPreviousPage={t39} /></ErrorBoundary>;
    $[112] = attempts_response;
    $[113] = current_page;
    $[114] = is_loading;
    $[115] = onAttemptSelected;
    $[116] = onHistoryAttemptSelected;
    $[117] = retryAttempt;
    $[118] = retrying_attempt_id;
    $[119] = selected_attempt_history_response;
    $[120] = selected_attempt_id;
    $[121] = selected_history_attempt_id;
    $[122] = t38;
    $[123] = t39;
    $[124] = t40;
  } else {
    t40 = $[124];
  }
  let t41;
  if ($[125] !== scrolled_element_ref || $[126] !== t37 || $[127] !== t40) {
    t41 = <Div style={t34} flex={t35} ref={scrolled_element_ref}>{t37}{t40}</Div>;
    $[125] = scrolled_element_ref;
    $[126] = t37;
    $[127] = t40;
    $[128] = t41;
  } else {
    t41 = $[128];
  }
  let t42;
  if ($[129] !== t33 || $[130] !== t41) {
    t42 = <StyledViewContent>{t33}{t41}</StyledViewContent>;
    $[129] = t33;
    $[130] = t41;
    $[131] = t42;
  } else {
    t42 = $[131];
  }
  let t43;
  if ($[132] !== onNextAttempt || $[133] !== onPrevAttempt || $[134] !== retryAttempt || $[135] !== retrying_attempt_id || $[136] !== sidebar_attempt || $[137] !== sidebar_attempt_id || $[138] !== updateSearchQuery) {
    t43 = sidebar_attempt_id && <OperatorEventAttemptSidebar attempt_id={sidebar_attempt_id} list_attempt={sidebar_attempt} onPrev={onPrevAttempt} onNext={onNextAttempt} onRetryAttempt={retryAttempt} retrying_attempt_id={retrying_attempt_id} onClose={() => updateSearchQuery({}, {
      remove_keys: ["selected_attempt_id", "selected_history_attempt_id"]
    })} />;
    $[132] = onNextAttempt;
    $[133] = onPrevAttempt;
    $[134] = retryAttempt;
    $[135] = retrying_attempt_id;
    $[136] = sidebar_attempt;
    $[137] = sidebar_attempt_id;
    $[138] = updateSearchQuery;
    $[139] = t43;
  } else {
    t43 = $[139];
  }
  let t44;
  if ($[140] !== t42 || $[141] !== t43) {
    t44 = <StyledViewLayout>{t42}{t43}</StyledViewLayout>;
    $[140] = t42;
    $[141] = t43;
    $[142] = t44;
  } else {
    t44 = $[142];
  }
  let t45;
  if ($[143] !== filters || $[144] !== onFilterChanged || $[145] !== t44) {
    t45 = <Filters.Provider route="operator-event-deliveries" filters={filters} components={filter_components} onFilterChanged={onFilterChanged}>{t44}</Filters.Provider>;
    $[143] = filters;
    $[144] = onFilterChanged;
    $[145] = t44;
    $[146] = t45;
  } else {
    t45 = $[146];
  }
  let t46;
  if ($[147] !== has_scrolled || $[148] !== t27 || $[149] !== t45) {
    t46 = <TableProvider initialConfig={t27} has_scrolled={has_scrolled}>{t45}</TableProvider>;
    $[147] = has_scrolled;
    $[148] = t27;
    $[149] = t45;
    $[150] = t46;
  } else {
    t46 = $[150];
  }
  return t46;
};
export default OperatorEventDeliveriesView;